import {
    Autocomplete, Avatar, AvatarGroup, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, InputBase, Modal, Tab, Tabs, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material'
import { Close, DeleteOutline, PictureAsPdf, Search, Visibility } from '@mui/icons-material'
import { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useDropzone } from 'react-dropzone'
import { Viewer, Worker } from '@react-pdf-viewer/core'
import { ToolContext } from '../../../core/context/ToolContext'
import { rqx } from '../../../core/request/API'
import { env } from '../../../core/Env'
import { uploadToS3Presigned } from '../../../core/transmit/S3'
import { Confirmation } from '../../../core/global/forms/CstmModal'
import Add from '../../../assets/images/icons/add.png'
import Pdf from '../../../assets/images/icons/pdf.png'
import { FormatFilesize, NoFound, UseStyles } from '../../mycloud/global/DesktopDsg'
import { NoResult } from '../../empty/Index'
import { Loader } from '../../fetching/Loader'
import {
    agreeGuidelineVersions, getGuidelineAgreeCount, getGuidelineAgreeList,
    readGuidelines, updateGuidelineVersion, viewGuidelineVersion
} from '../api/guidelines'

let uai_user = process.env.REACT_APP_UAI_REGULAR

const statusName = status => parseInt(status) === 2 ? 'Issued' : 'Uploaded'

const normalizeVersion = (version, latestVersionId) => ({
    ...version,
    versionNo: version.version_number || 1,
    status: statusName(version.status),
    dateupload: version.dateupload || version.created_at,
    dateissued: version.issue_date || version.dateissued,
    preparedByName: version.prepared_name || version.preparedby_name || version.preparedby,
    approvedByName: version.approved_name || version.approvedby_name || version.approvedby,
    file_name: version.file_name || version.name,
    file_size: version.file_size || version.size,
    file_link: version.file_link || version.link,
    isLatest: String(version.id) === String(latestVersionId),
    isPreviousVersion: parseInt(version.status) === 2 &&
        String(version.id) !== String(latestVersionId),
    acknowledgementStatus: parseInt(version.cp_status) === 3 ? 'Agreed' : 'Pending',
    acknowledgedAt: version.agreed_at || null,
    agreedEmployees: version.agreedEmployees || []
})

const canCreateVersion = guideline =>
    guideline.versions.some(version => version.isLatest && version.status === 'Issued') &&
    !guideline.versions.some(version => version.status === 'Uploaded')

const Badge = ({ label, latest }) => <Chip size="small" label={label} sx={{
    height: 24, fontSize: 12, fontWeight: 600,
    color: latest || label === 'Issued' ? '#107038' : '#285C8C',
    bgcolor: latest || label === 'Issued' ? '#E2F3E9' : '#E7F2FC'
}} />

const Info = ({ label, value }) => <Box display="grid" gridTemplateColumns="1fr 1.1fr" gap={1} mb={2}>
    <Box color="#737373" fontSize={13}>{label}</Box>
    <Box color="#333333" fontSize={13}>{value === 0 ? 0 : value || '—'}</Box>
</Box>

const PdfPreview = ({ version }) => version.file_link ?
    <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js">
        <Box height={{ xs: 420, md: 560 }} bgcolor="#F8F9FA"
            borderRadius="8px" overflow="auto">
            <Viewer fileUrl={version.file_link} />
        </Box>
    </Worker>
:
    <Box height="100%" minHeight={350} bgcolor="#F8F9FA"
        borderRadius="8px" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <PictureAsPdf sx={{ fontSize: 64, color: '#C74040' }} />
        <Typography mt={1} fontWeight={600}>{version.file_name}</Typography>
    </Box>

const EmployeeList = ({ open, version, onClose }) => <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
    <DialogTitle display="flex" justifyContent="space-between">
        <Typography fontSize={20} fontWeight={600}>Employees Who Agreed</Typography>
        <IconButton onClick={onClose}><Close /></IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ minHeight: 240 }}>
        {version?.agreedEmployees.map(employee => <Box key={employee.id} display="flex" alignItems="center" gap={1.5} mb={2}>
            <Avatar src={employee.profile} sx={{ width: 32, height: 32 }} />
            <Typography fontSize={14}>{employee.uname}</Typography>
        </Box>)}
        {!version?.agreedEmployees.length && <Typography color="text.secondary">No employees have agreed to this version.</Typography>}
    </DialogContent>
</Dialog>

const VersionHistory = ({ guideline, isRegular, onView, onIssueClick }) => <TableContainer>
    <Table size="small">
        <TableHead><TableRow>
            {(isRegular
                ? ['Version', 'Date of Upload', 'Issue Date', 'Prepared By', 'Approved By']
                : ['Version', 'Date of Upload', 'Issue Date', 'Status', 'Prepared By', 'Approved By', 'Indicator', 'Action']
            ).map(label =>
                <TableCell key={label} sx={{ fontWeight: 600 }}>{label}</TableCell>)}
        </TableRow></TableHead>
        <TableBody>{[...guideline.versions]
            .filter(version => !isRegular || version.status === 'Issued')
            .sort((a, b) => b.versionNo - a.versionNo)
            .map(version =>
            <TableRow key={version.id}>
                <TableCell><Badge label={`Version ${version.versionNo}`} /></TableCell>
                <TableCell>{moment(version.dateupload).format('MMM D, YYYY')}</TableCell>
                <TableCell>{version.dateissued ? moment(version.dateissued).format('MMM D, YYYY') : '—'}</TableCell>
                {!isRegular && <TableCell>{version.status === 'Uploaded' ?
                    <Button size="small" onClick={() => onIssueClick(version)}><Badge label="Uploaded" /></Button> :
                    <Badge label="Issued" />}</TableCell>}
                <TableCell>{version.preparedByName}</TableCell>
                <TableCell>{version.approvedByName}</TableCell>
                {!isRegular && <>
                    <TableCell>{version.isLatest ? <Badge latest label="Latest" /> :
                        version.isPreviousVersion ? <Badge label="Previous Version" /> : '—'}</TableCell>
                    <TableCell><Button size="small" onClick={() => onView(version)}>View</Button></TableCell>
                </>}
            </TableRow>)}</TableBody>
    </Table>
</TableContainer>

const GuidelineView = ({
    open, guideline, version, isRegular, onClose, onCreateVersion, onViewVersion, onIssueClick,
    onAgree, loading
}) => {
    const [tab, setTab] = useState(0)
    const [employeesOpen, setEmployeesOpen] = useState(false)
    const [agreementChecked, setAgreementChecked] = useState(false)
    useEffect(() => {
        setAgreementChecked(false)
    }, [open, version?.id])
    if (!guideline || !version) return null
    const preparing = guideline.versions.some(item => item.status === 'Uploaded')

    return <>
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography fontSize={20} fontWeight={700}>{guideline.policyname}</Typography>
                    <Typography fontSize={13} color="text.secondary">
                        {guideline.series_num} · Version {version.versionNo}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {!isRegular && canCreateVersion(guideline) && version.isLatest &&
                        <Button size="small" variant="contained" onClick={onCreateVersion}
                            sx={{ bgcolor: '#107038' }}>Create New Version</Button>}
                    <IconButton onClick={onClose}><Close /></IconButton>
                </Box>
            </DialogTitle>
            <Tabs value={tab} onChange={(event, value) => setTab(value)}
                sx={{ px: 3, borderBottom: '1px solid #E6E6E6' }}>
                <Tab label="Guideline Details" /><Tab label="Version History" />
            </Tabs>
            <DialogContent sx={{
                minHeight: 610,
                overflowY: { xs: 'auto', md: 'hidden' }
            }}>
                {loading ? <Loader /> : tab === 0 ?
                    <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'minmax(0,2fr) 350px' }} gap={3}>
                        <Box>
                            <Typography fontWeight={600}>Description</Typography>
                            <Typography fontSize={14} color="text.secondary" my={1}>{version.description}</Typography>
                            <PdfPreview version={version} />
                            {isRegular && version.acknowledgementStatus !== 'Agreed' &&
                                <Box display="flex" justifyContent="center" flexDirection="column"
                                    alignItems="center" pb={2}>
                                    <Box display="flex" justifyContent="center" alignItems="center"
                                        p={2} m={2} gap="8px" fontSize="14px" lineHeight="20px">
                                        <Checkbox checked={agreementChecked}
                                            onChange={event => setAgreementChecked(event.target.checked)}
                                            inputProps={{ 'aria-label': 'Acknowledge guideline in view' }} />
                                        <Box>
                                            I acknowledge that I have read this guideline in its entirety and
                                            will seek clarification for anything I do not understand.
                                        </Box>
                                    </Box>
                                    <Button variant="contained" disabled={!agreementChecked}
                                        onClick={async() => {
                                            const agreed = await onAgree(guideline, version)
                                            if (agreed) setAgreementChecked(false)
                                        }}
                                        sx={{ color: '#00B74C', bgcolor: '#00be811f',
                                            borderRadius: '10px', minWidth: 250,
                                            '&:hover': { bgcolor: '#00be812f' } }}>
                                        Understood and Agree
                                    </Button>
                                </Box>
                            }
                        </Box>
                        <Box bgcolor="#F8F9FA" borderRadius="8px" p={3}
                            position={{ xs: 'static', md: 'sticky' }} top={0} alignSelf="flex-start">
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                                <Typography fontWeight={700}>Guideline Information</Typography>
                            </Box>
                            {!isRegular && preparing && <Box p={1.5} mb={2} bgcolor="#FFF4D6" borderRadius="8px" fontSize={12}>
                                A new version is already being prepared.
                            </Box>}
                            {isRegular ?
                                <>
                                    <Info label="Policy Number" value={guideline.series_num} />
                                    <Info label="Department" value={guideline.dept_name} />
                                    <Info label="Date Issued" value={version.dateissued && moment(version.dateissued).format('MMMM D, YYYY')} />
                                    <Info label="Prepared By" value={version.preparedByName} />
                                    <Info label="Approved By" value={version.approvedByName} />
                                </>
                            :
                                <>
                                <Info label="Guideline Number" value={guideline.series_num} />
                                <Info label="Department" value={guideline.dept_name} />
                                <Info label="Version" value={`Version ${version.versionNo}`} />
                                <Info label="Status" value={version.status} />
                                <Info label="Date of Upload" value={moment(version.dateupload).format('MMMM D, YYYY')} />
                                <Info label="Issue Date" value={version.dateissued && moment(version.dateissued).format('MMMM D, YYYY')} />
                                <Info label="Prepared By" value={version.preparedByName} />
                                <Info label="Approved By" value={version.approvedByName} />
                                <Info label="Number of Agree"
                                    value={version.agreeCount ?? version.agreedEmployees.length} />
                                <Box display="grid" gridTemplateColumns="1fr 1.1fr" gap={1} alignItems="center">
                                    <Box color="#737373" fontSize={13}>Employees Who Agreed</Box>
                                    <AvatarGroup max={3} onClick={() => setEmployeesOpen(true)}
                                        sx={{ justifyContent: 'flex-end', cursor: 'pointer', '& .MuiAvatar-root': { width: 32, height: 32, fontSize: 12 } }}>
                                        {version.agreedEmployees.map(employee =>
                                            <Avatar key={employee.id} src={employee.profile}>{employee.uname[0]}</Avatar>)}
                                    </AvatarGroup>
                                </Box>
                                </>
                            }
                        </Box>
                    </Box>
                :
                    <VersionHistory guideline={guideline} isRegular={isRegular}
                        onView={item => { onViewVersion(item); setTab(0) }} onIssueClick={onIssueClick} />}
            </DialogContent>
        </Dialog>
        <EmployeeList open={employeesOpen} version={version} onClose={() => setEmployeesOpen(false)} />
    </>
}

const emptyVersionForm = () => ({
    fileName: '', fileType: '', fileSize: '', file: null, description: '', dateupload: new Date(),
    dateissued: null, preparedby: '', preparedByName: '', approvedby: '', approvedByName: ''
})

const CreateVersion = ({ open, guideline, onCancel, onCreate }) => {
    const { tool_state } = useContext(ToolContext)
    const dsg = UseStyles()
    const latest = guideline?.versions.find(version => version.isLatest) ||
        [...(guideline?.versions || [])]
            .filter(version => version.status === 'Issued')
            .sort((a, b) => b.versionNo - a.versionNo)[0] ||
        guideline?.versions?.[0]
    const next = guideline?.versions?.length ?
        Math.max(...guideline.versions.map(version => version.versionNo || 1)) + 1 : 1
    const [form, setForm] = useState(emptyVersionForm)
    const [confirm, setConfirm] = useState(false)
    const [creating, setCreating] = useState(false)
    const [allUsers, setAllUsers] = useState([])
    const [fileError, setFileError] = useState('')
    const handleCancel = () => {
        if (creating) return
        setForm(emptyVersionForm())
        setConfirm(false)
        setFileError('')
        onCancel()
    }
    useEffect(() => {
        if (!open) return
        setForm(emptyVersionForm())
        setConfirm(false)
        setCreating(false)
        setFileError('')
        const loadUsers = async() => {
            const access = tool_state.__SESSION.data.uaccess[0]
            const response = await rqx.get(`<<global>>/${env()}/tooluser/gettooluser`, {
                company_id: access.company_id || 0, tool_id: process.env.REACT_APP_TID, ref: 'active'
            })
            const users = [...(response.superadmin || []), ...(response.users || [])]
                .map(user => ({ ...user, uname: `${user.first_name} ${user.last_name}` }))
            setAllUsers(users)
        }
        loadUsers()
    }, [open, tool_state.__SESSION.data.uaccess])
    const onDrop = files => {
        if (!files.length) {
            setFileError('Uploading is restricted to PDF file formats exclusively.')
            return
        }
        if (files[0].size > 4194304) {
            setFileError('File uploads must not exceed 3 MB.')
            return
        }
        setFileError('')
        setForm(value => ({
            ...value, file: files, fileName: files[0].name,
            fileType: files[0].type, fileSize: files[0].size
        }))
    }
    const { getRootProps, getInputProps } = useDropzone({
        onDrop, accept: { 'application/pdf': ['.pdf'] }, multiple: false
    })
    if (!guideline || !latest) return null
    const change = event => setForm(value => ({ ...value, [event.target.name]: event.target.value }))
    const valid = form.fileName && form.description && form.dateupload && form.dateissued &&
        form.preparedByName && form.approvedByName
    return <>
        <Modal open={open}>
            <Box height="100%" width="100%" bgcolor="#FFFFFF" overflow="auto">
                <Box minHeight="100%" width="100%" display={{ xs: 'block', md: 'flex' }} alignItems="stretch">
                    <Box minHeight={{ xs: 'auto', md: '100vh' }} width="100%" display="flex"
                        flexDirection="column" justifyContent="center" p={{ xs: '32px 24px', md: '40px 60px' }}>
                        <Button variant="contained" disableElevation disabled={creating} onClick={handleCancel}
                            sx={{ width: 80, p: '12px 16px', color: '#333333', borderRadius: '8px',
                                bgcolor: '#F8F9FA', '&:hover': { bgcolor: '#E2EDE7', color: '#107038' } }}>
                            Cancel
                        </Button>
                        <Box display="flex" alignItems="center" mt="40px" mb="32px">
                            <Typography fontSize={20} fontWeight={600}>Create New Guideline Version</Typography>
                            <Typography fontSize={12} color="#737373" ml="12px">
                                Guideline Number: {guideline.series_num}
                            </Typography>
                        </Box>
                        <Box width="100%" mt="16px" mb="8px">
                            <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>Department Name*</Box>
                            <TextField fullWidth disabled value={guideline.dept_name}
                                className={dsg.txtfld} inputProps={{ style: { padding: '14px 16px' } }} />
                        </Box>
                        <Box width="100%" mt="16px" mb="8px" display="flex">
                            <Box width="100%">
                                <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>Current Version</Box>
                                <TextField fullWidth disabled value={`Version ${latest.versionNo}`}
                                    className={dsg.txtfld} inputProps={{ style: { padding: '14px 16px' } }} />
                            </Box>
                            <Box width="100%" pl="20px">
                                <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>New Version</Box>
                                <TextField fullWidth disabled value={`Version ${next}`}
                                    className={dsg.txtfld} inputProps={{ style: { padding: '14px 16px' } }} />
                            </Box>
                        </Box>
                        <Box minHeight="272px" height="272px" bgcolor="#F8F9FA" borderRadius="12px"
                            p={2} mt={4} display="flex" alignItems="center" justifyContent="center" className="overflowY ubr-upload">
                            {form.fileName ?
                                <Box display="flex" alignItems="center" width="100%" p="9px 13px"
                                    bgcolor="#FFFFFF" borderRadius="6px"
                                    boxShadow="0px 0px 4px rgba(0, 0, 0, 0.04), 0px 4px 8px rgba(0, 0, 0, 0.06)">
                                    <img src={Pdf} alt="PDF" />
                                    <Box display="flex" flexDirection="column" ml="13px">
                                        <Box color="#333333" fontWeight={600} fontSize={12} lineHeight="16px">
                                            {form.fileName}
                                        </Box>
                                        <Box color="#737373" fontWeight={400} fontSize={10} lineHeight="16px">
                                            {FormatFilesize(form.fileSize)}
                                        </Box>
                                    </Box>
                                </Box>
                            :
                                <Box {...getRootProps({ className: 'dropzone' })} border="2px dashed #D9D9D9"
                                    height="100%" width="100%" display="flex" alignItems="center"
                                    justifyContent="center" flexDirection="column" className="c-pointer">
                                    <img src={Add} alt="Add PDF" />
                                    <Box mt={5} fontSize={14} fontWeight={400} lineHeight="19px">
                                        Drag and drop a pdf file, or Browse
                                    </Box>
                                    <Box mt="12px" fontSize={12} fontWeight={400} lineHeight="19px">
                                        Maximum file size is 3 MB.
                                    </Box>
                                    <input {...getInputProps()} />
                                </Box>
                            }
                        </Box>
                        {fileError && <Box color="#D32F2F" fontSize={12} mt={1}>{fileError}</Box>}
                        {form.fileName && <Box display="flex" justifyContent="center" mt={3}>
                            <Button {...getRootProps({ className: 'dropzone' })}
                                sx={{ bgcolor: '#F8F9FA', border: '1px solid #E6E6E6', color: '#333333',
                                    p: '12px 16px', lineHeight: '16px', borderRadius: '6px' }}>
                                Change
                                <input {...getInputProps()} />
                            </Button>
                        </Box>}
                    </Box>
                    <Box bgcolor="#F8F9FA" minHeight={{ xs: 'auto', md: '100vh' }} width="100%"
                        display="flex" flexDirection="column" justifyContent="center" p={{ xs: '32px 24px', md: '40px 60px' }}>
                        <Box width="100%" height="100%" mb="8px" display="flex" flexDirection="column" justifyContent="space-between">
                            <Box width="100%" height="100%" display="flex" justifyContent="center" flexDirection="column">
                                <Box color="#107038" fontSize="14px" fontWeight="600" lineHeight="16px" mb="16px">Guideline Details</Box>
                                <Box>
                                    <Box width="100%" mt="10px" mb="8px">
                                        <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>Guideline Name*</Box>
                                        <TextField fullWidth disabled value={guideline.policyname}
                                            className={dsg.txtfld} inputProps={{ style: { padding: '14px 16px' } }} />
                                    </Box>
                                    <Box width="100%" mt="16px" lineHeight="16px" mb="8px">
                                        <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>Guideline Description*</Box>
                                        <TextField fullWidth required multiline rows={3} name="description"
                                            sx={{ bgcolor: '#FFFFFF' }} value={form.description} onChange={change} />
                                        <Box color="#A2A3A9" fontSize="12px" display="flex" justifyContent="flex-end">
                                            {form.description.length}/1000
                                        </Box>
                                    </Box>
                                    <Box width="100%" mt="16px" lineHeight="16px" mb="8px" display="flex">
                                        <Box width="100%">
                                            <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>Date of Upload*</Box>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker disabled value={form.dateupload}
                                                    renderInput={params => <TextField {...params} fullWidth className={dsg.txtfld} />} />
                                            </LocalizationProvider>
                                        </Box>
                                        <Box width="100%" pl="20px">
                                            <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>Issue Date*</Box>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker value={form.dateissued}
                                                    onChange={value => setForm(current => ({ ...current, dateissued: value }))}
                                                    renderInput={params => <TextField {...params} fullWidth required className={dsg.txtfld} />} />
                                            </LocalizationProvider>
                                        </Box>
                                    </Box>
                                    <Box width="100%" mt="16px" lineHeight="16px" mb="8px" display="flex">
                                        <Box width="100%">
                                            <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>Prepared By*</Box>
                                            <Autocomplete noOptionsText="No Result" autoHighlight options={allUsers}
                                                getOptionLabel={option => option.uname}
                                                value={allUsers.find(user => user.uname === form.preparedByName) || null}
                                                onChange={(event, value) => setForm(current => ({
                                                    ...current, preparedby: value?.id || '',
                                                    preparedByName: value?.uname || ''
                                                }))}
                                                renderInput={params => <TextField {...params} fullWidth required className={dsg.plcyTxt} />} />
                                        </Box>
                                        <Box width="100%" pl="20px">
                                            <Box fontSize="14px" fontWeight={600} lineHeight="16px" mb={1}>Approved By*</Box>
                                            <Autocomplete noOptionsText="No Result" autoHighlight options={allUsers}
                                                getOptionLabel={option => option.uname}
                                                value={allUsers.find(user => user.uname === form.approvedByName) || null}
                                                onChange={(event, value) => setForm(current => ({
                                                    ...current, approvedby: value?.id || '',
                                                    approvedByName: value?.uname || ''
                                                }))}
                                                renderInput={params => <TextField {...params} fullWidth required className={dsg.plcyTxt} />} />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            <Box display="flex" justifyContent="flex-end">
                                <Button disabled={!valid || creating} variant="contained" disableElevation
                                    className={dsg.btnNext} onClick={() => setConfirm(true)}
                                    sx={{ backgroundColor: !valid && '#C7DAD7',
                                        '&:hover': { backgroundColor: !valid && '#C7DAD7' } }}>
                                    Next
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
        <Dialog open={confirm} onClose={creating ? null : () => setConfirm(false)} fullWidth maxWidth="xs">
            <DialogTitle sx={{ p: '16px 24px' }}>Create New Version?</DialogTitle>
            <DialogContent sx={{ p: '16px 24px' }}>
                <Typography>A new Uploaded version will be created. The current Issued version remains visible to regular users.</Typography>
                <Box mt={2}><Info label="Guideline" value={guideline.policyname} />
                    <Info label="Current Version" value={`Version ${latest.versionNo}`} />
                    <Info label="New Version" value={`Version ${next}`} /></Box>
            </DialogContent>
            <DialogActions sx={{ p: '16px 24px' }}>
                <Button disabled={creating} onClick={() => setConfirm(false)}>Cancel</Button>
                <Button variant="contained" disabled={creating} onClick={async() => {
                    if (creating) return
                    setCreating(true)
                    try {
                        const created = await onCreate(form)
                        if (created) {
                            setConfirm(false)
                            setForm(emptyVersionForm())
                        }
                    } finally {
                        setCreating(false)
                    }
                }} startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ bgcolor: '#107038' }}>
                    {creating ? 'Creating Version...' : 'Create Version'}
                </Button>
            </DialogActions>
        </Dialog>
    </>
}

const IssueConfirm = ({ version, open, onCancel, onIssue }) => <Confirmation
    isOpen={open}
    disabled={false}
    message={version?.versionNo > 1
        ? `Issue Version ${version.versionNo} of this Guideline?`
        : 'Update Guideline Status to Issued?'}
    text={version?.versionNo > 1 ? 'Issue' : 'Update'}
    proceedBtn={onIssue}
    cancelBtn={onCancel}
/>

const AgreeConfirmation = ({ open, guideline, version, onCancel, onAgree }) => {
    const [checked, setChecked] = useState(false)
    const handleCancel = () => {
        setChecked(false)
        onCancel()
    }
    const handleAgree = async() => {
        const agreed = await onAgree(guideline, version)
        if (agreed) {
            setChecked(false)
        }
    }
    return <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
            <Box>
                <Typography fontSize={20} fontWeight={600}>Guideline Acknowledgement</Typography>
                <Typography fontSize={13} color="text.secondary">
                    {guideline?.series_num} · Version {version?.versionNo}
                </Typography>
            </Box>
            <IconButton onClick={handleCancel}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
            <Typography fontWeight={600}>{guideline?.policyname}</Typography>
            <Box display="flex" alignItems="flex-start" gap={1} mt={3}>
                <Checkbox checked={checked} onChange={event => setChecked(event.target.checked)}
                    inputProps={{ 'aria-label': 'Acknowledge guideline' }} />
                <Typography fontSize={14} lineHeight="20px" pt="9px">
                    I acknowledge that I have read this guideline in its entirety and will seek clarification
                    for anything I do not understand.
                </Typography>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button variant="contained" disabled={!checked} onClick={handleAgree}
                sx={{ bgcolor: '#107038' }}>
                Understood and Agree
            </Button>
        </DialogActions>
    </Dialog>
}

export const Index = ({ deptStaticData = [] }) => {
    const { tool_state } = useContext(ToolContext)
    const access = tool_state.__SESSION.data.uaccess[0]
    const isRegular = access.role_id === parseInt(uai_user)
    const userId = parseInt(atob(tool_state.__SESSION.data.ainfo.aid).split('_', 2)[1])
    const [guidelines, setGuidelines] = useState([])
    const [users, setUsers] = useState([])
    const [search, setSearch] = useState('')
    const [selectedParent, setSelectedParent] = useState(null)
    const [selectedVersionId, setSelectedVersionId] = useState(null)
    const [dialog, setDialog] = useState('')
    const [issueTarget, setIssueTarget] = useState(null)
    const [removeTarget, setRemoveTarget] = useState(null)
    const [agreeTarget, setAgreeTarget] = useState(null)
    const [selectedAgreementIds, setSelectedAgreementIds] = useState([])
    const [bulkAgreementChecked, setBulkAgreementChecked] = useState(false)
    const [loading, setLoading] = useState(true)
    const [viewLoading, setViewLoading] = useState(false)
    const rows = useMemo(() => {
        const guidelineRows = guidelines.map(guideline => ({
            guideline,
            version: guideline.versions[0]
        }))
        return isRegular ? guidelineRows.sort((a, b) => {
            const dateDifference = new Date(b.version.issue_date || b.version.dateissued || 0) -
                new Date(a.version.issue_date || a.version.dateissued || 0)
            return dateDifference || Number(b.version.id) - Number(a.version.id)
        }) : guidelineRows
    }, [guidelines, isRegular])
    const selected = guidelines.find(item => item.parentId === selectedParent)
    const selectedVersion = selected?.versions.find(item => item.id === selectedVersionId)

    const userName = id => {
        const user = users.find(item => item.id === id)
        return user ? user.uname : id
    }

    const loadGuidelines = async filter => {
        setLoading(true)
        try {
            const response = await readGuidelines({
                isRegular,
                companyId: access.company_id,
                departmentIds: [],
                departmentOwnedId: access.company_department_id,
                userId,
                lastId: 0,
                limit: 100,
                filter
            })
            const result = Array.isArray(response) ? response : []
            setGuidelines(result.map(row => ({
                parentId: row.parent_id || row.id,
                series_num: row.policy_num || row.series_num,
                policyname: row.policy_name || row.policyname,
                departmentId: row.company_department_id,
                dept_name: row.dept_name || row.department_name ||
                    deptStaticData.find(department => department.id === row.company_department_id)?.name || 'All',
                versions: [normalizeVersion(row, parseInt(row.status) === 2 ? row.id : null)]
            })))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const loadUsers = async() => {
            const response = await rqx.get(`<<global>>/${env()}/tooluser/gettooluser`, {
                company_id: access.company_id || 0,
                tool_id: process.env.REACT_APP_TID,
                ref: 'active'
            })
            setUsers([...(response.superadmin || []), ...(response.users || [])].map(user => ({
                ...user,
                uname: `${user.first_name} ${user.last_name}`
            })))
        }
        loadUsers()
    }, [access.company_id])

    useEffect(() => {
        const timer = setTimeout(() => loadGuidelines(search), 400)
        return () => clearTimeout(timer)
        // eslint-disable-next-line
    }, [search, isRegular, access.company_id, access.company_department_id, userId, deptStaticData])

    const loadAgreements = async version => {
        const [count, agreementRows] = await Promise.all([
            getGuidelineAgreeCount(version.id),
            getGuidelineAgreeList(version.id)
        ])
        return {
            ...version,
            agreeCount: Number(count) || 0,
            agreedEmployees: (agreementRows || []).map(agreement => {
                const user = users.find(item => item.id === agreement.user_id)
                return {
                    ...agreement,
                    id: agreement.user_id,
                    uname: user ? user.uname : 'Inactive User',
                    profile: user?.profile ? JSON.parse(user.profile).profile : ''
                }
            })
        }
    }

    const openView = async(guideline, version) => {
        setSelectedParent(guideline.parentId)
        setSelectedVersionId(version.id)
        setDialog('view')
        setViewLoading(true)
        try {
            const response = await viewGuidelineVersion(version.id)
            const detail = Array.isArray(response) ? response[0] : response
            if (!detail) return
        const familyRows = [detail, ...(detail.versions || [])]
            .filter((item, index, items) => items.findIndex(value => value.id === item.id) === index)
            .map(item => String(item.id) === String(version.id) ? {
                ...item,
                cp_status: item.cp_status ?? version.cp_status ??
                    (version.acknowledgementStatus === 'Agreed' ? 3 : undefined),
                agreed_at: item.agreed_at || version.agreed_at || version.acknowledgedAt
            } : item)
        const latestIssued = [...familyRows]
            .filter(item => parseInt(item.status) === 2)
            .sort((a, b) => (b.version_number || 1) - (a.version_number || 1))[0]
        const versions = familyRows.map(item => normalizeVersion({
            ...item,
            prepared_name: item.prepared_name || userName(item.preparedby),
            approved_name: item.approved_name || userName(item.approvedby)
        }, latestIssued?.id))
        const requested = await loadAgreements(versions.find(item => item.id === version.id) || versions[0])
        const hydratedVersions = versions.map(item => item.id === requested.id ? requested : item)
        const parentId = detail.parent_id || detail.id
        const hydratedGuideline = {
            parentId,
            series_num: detail.policy_num || detail.series_num,
            policyname: detail.policy_name || detail.policyname,
            departmentId: detail.company_department_id,
            dept_name: detail.dept_name || detail.department_name || guideline.dept_name,
            versions: hydratedVersions
        }
        setGuidelines(current => {
            const exists = current.some(item => item.parentId === guideline.parentId)
            return exists ? current.map(item =>
                item.parentId === guideline.parentId ? hydratedGuideline : item
            ) : [...current, hydratedGuideline]
        })
            setSelectedParent(parentId)
            setSelectedVersionId(requested.id)
        } finally {
            setViewLoading(false)
        }
    }
    const askIssue = version => { setIssueTarget(version); setDialog('issue') }
    const agreeToVersion = async(guideline, version) => {
        const response = await agreeGuidelineVersions({
            userId,
            ids: [{ cpid: version.id, status: 3 }]
        })
        if (response.message === 'success') {
            setGuidelines(current => current.map(item =>
                String(item.parentId) === String(guideline.parentId) ? {
                ...item,
                versions: item.versions.map(itemVersion =>
                    String(itemVersion.id) === String(version.id) ? {
                    ...itemVersion,
                    acknowledgementStatus: 'Agreed',
                    acknowledgedAt: new Date().toISOString(),
                    agreedEmployees: [
                        ...(itemVersion.agreedEmployees || []),
                        {
                            id: userId,
                            uname: userName(userId) || 'Current User',
                            profile: ''
                        }
                    ]
                } : itemVersion)
            } : item))
            setAgreeTarget(null)
            return true
        }
        return false
    }
    const toggleAgreementSelection = versionId => {
        setBulkAgreementChecked(false)
        setSelectedAgreementIds(current => current.includes(versionId)
            ? current.filter(id => id !== versionId)
            : [...current, versionId])
    }
    const selectableVersionIds = isRegular ? rows
        .filter(({ version }) => version.acknowledgementStatus !== 'Agreed')
        .map(({ version }) => version.id) : []
    const allPendingSelected = selectableVersionIds.length > 0 &&
        selectableVersionIds.every(id => selectedAgreementIds.includes(id))
    const toggleAllAgreements = () => {
        setBulkAgreementChecked(false)
        setSelectedAgreementIds(allPendingSelected ? [] : selectableVersionIds)
    }
    const agreeToSelectedVersions = async() => {
        const responses = await Promise.all(selectedAgreementIds.map(id =>
            agreeGuidelineVersions({ userId, ids: [{ cpid: id, status: 3 }] })
        ))
        if (responses.every(response => response.message === 'success')) {
            const agreedIds = selectedAgreementIds.map(String)
            const agreedAt = new Date().toISOString()
            setGuidelines(current => current.map(guideline => ({
                ...guideline,
                versions: guideline.versions.map(version => agreedIds.includes(String(version.id)) ? {
                    ...version,
                    cp_status: 3,
                    acknowledgementStatus: 'Agreed',
                    agreed_at: agreedAt,
                    acknowledgedAt: agreedAt
                } : version)
            })))
            setSelectedAgreementIds([])
            setBulkAgreementChecked(false)
        }
    }
    const createGuidelineVersion = async form => {
        const parentId = selected.parentId || selected.id
        const uploadedFile = await uploadToS3Presigned(form.file, {
            requestRef: 'presigned-upload',
            feature_ref: 'policies',
            tool_id: process.env.REACT_APP_TOOL_ID,
            privacy: 'public'
        })
        const payload = {
            parent_id: parentId,
            company_id: access.company_id,
            description: form.description,
            dateissued: moment(form.dateissued).format('YYYY-MM-DD'),
            preparedby: form.preparedby,
            approvedby: form.approvedby,
            file_name: form.fileName,
            file_type: form.fileType,
            file_size: form.fileSize,
            file_link: uploadedFile.rqxPreSigned.location,
            user_id: parseInt(atob(tool_state.__SESSION.data.ainfo.aid).split('_', 2)[1])
        }
        const response = await rqx.post(
            `<<tool>>/${env() === 'dev' ? 'local' : env()}/general-maintenance/cloud/policy/create`,
            payload
        )
        if (response.message === 'success') {
            await loadGuidelines(search)
            setDialog('')
            return true
        }
        return false
    }


    return <Box height="100%" bgcolor="#FFF" borderRadius="12px" p={3}
        overflow="hidden" position="relative" display="flex" flexDirection="column">
        <InputBase value={search} onChange={event => setSearch(event.target.value)} placeholder="Search"
            endAdornment={<Search />} sx={{ border: '2px solid #D9EBE8', borderRadius: '10px', px: 2, width: 300, height: 46, mb: 3 }} />
        {loading ? <Loader /> : guidelines.length === 0 ?
            (search ? <NoResult /> :
                <NoFound msg={isRegular ? 'No Guideline Found.' : 'No Guideline Found. Upload Now!'} />)
        : <TableContainer sx={{
            flex: 1,
            overflow: 'auto',
            pb: isRegular && selectedAgreementIds.length > 0 ? '170px' : 0
        }}><Table sx={{ minWidth: 900 }}>
            <TableHead><TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {isRegular && <Box width={34} minWidth={34} display="flex" justifyContent="center">
                            {selectableVersionIds.length > 0 &&
                                <Checkbox size="small" checked={allPendingSelected}
                                    indeterminate={selectedAgreementIds.length > 0 && !allPendingSelected}
                                    onChange={toggleAllAgreements}
                                    inputProps={{ 'aria-label': 'Select all pending guidelines' }} />}
                        </Box>}
                        Guideline No.
                    </Box>
                </TableCell>
                {[
                    'Department', 'Guideline Name', 'Issue Date', 'Version',
                    ...(!isRegular ? ['Status'] : []), 'Action'
                ].map(label =>
                    <TableCell key={label} sx={{ fontWeight: 600 }}>{label}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>{rows.map(({ guideline, version }) => <TableRow hover key={guideline.parentId}>
                <TableCell><Box display="flex" alignItems="center" gap={1}>
                    {isRegular && <Box width={34} minWidth={34} display="flex" justifyContent="center">
                        {version.acknowledgementStatus !== 'Agreed' &&
                            <Checkbox size="small" checked={selectedAgreementIds.includes(version.id)}
                                onChange={() => toggleAgreementSelection(version.id)}
                                inputProps={{ 'aria-label': `Select ${guideline.series_num}` }} />}
                    </Box>}
                    {guideline.series_num}
                </Box></TableCell><TableCell>{guideline.dept_name}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{guideline.policyname}</TableCell>
                <TableCell>{version.dateissued ? moment(version.dateissued).format('MMM D, YYYY') : '—'}</TableCell>
                <TableCell><Box display="flex" gap={.5}><Badge label={`Version ${version.versionNo}`} />
                    {!isRegular && version.isLatest && <Badge latest label="Latest" />}
                </Box></TableCell>
                {!isRegular && <TableCell>{version.status === 'Uploaded' ?
                    <Button size="small" onClick={() => { setSelectedParent(guideline.parentId); askIssue(version) }}><Badge label="Uploaded" /></Button> :
                    <Badge label="Issued" />}</TableCell>}
                <TableCell><Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="View"><IconButton onClick={() => openView(guideline, version)}><Visibility /></IconButton></Tooltip>
                    {isRegular && (version.acknowledgementStatus !== 'Agreed' ?
                        <Button size="small" onClick={() => setAgreeTarget({ guideline, version })}
                            sx={{ color: '#00B74C', bgcolor: '#00be811f', borderRadius: '10px' }}>
                            Agree
                        </Button>
                    :
                        <Button size="small" disableRipple sx={{ color: '#333333' }}>Agreed</Button>
                    )}
                    {!isRegular && <Tooltip title={version.status === 'Issued'
                        ? 'Only uploaded guidelines can be deleted. Issued guidelines cannot be deleted.'
                        : 'Remove uploaded guideline'}>
                        <Box component="span">
                            <IconButton disabled={version.status === 'Issued'}
                                onClick={() => setRemoveTarget(guideline)}>
                                <DeleteOutline sx={{ color: version.status === 'Issued' ? '#A2A3A9' : 'inherit' }} />
                            </IconButton>
                        </Box>
                    </Tooltip>}
                </Box></TableCell>
            </TableRow>)}</TableBody>
        </Table></TableContainer>}
        {isRegular && selectedAgreementIds.length > 0 &&
            <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center"
                position="absolute" left={0} right={0} bottom={0} zIndex={5}
                bgcolor="#FFFFFF" borderTop="1px solid #E6E6E6"
                boxShadow="0px -4px 12px rgba(0, 0, 0, 0.08)" px={3} pt={2} pb={2}>
                <Box display="flex" justifyContent="center" alignItems="center" gap="8px"
                    fontSize="14px" lineHeight="20px" maxWidth={760} textAlign="center" mb={2}>
                    <Checkbox checked={bulkAgreementChecked}
                        onChange={event => setBulkAgreementChecked(event.target.checked)}
                        inputProps={{ 'aria-label': 'Confirm selected guideline acknowledgement' }} />
                    <Box>
                        I acknowledge that I have read the selected guidelines in their entirety and will
                        seek clarification for anything I do not understand.
                    </Box>
                </Box>
                <Button onClick={agreeToSelectedVersions} disabled={!bulkAgreementChecked}
                    sx={{ color: '#00B74C', bgcolor: '#00be811f', borderRadius: '10px',
                        minWidth: 250, '&.Mui-disabled': { color: '#737373', bgcolor: '#E6E6E6' } }}>
                    Understood and Agree
                </Button>
            </Box>
        }
        <GuidelineView open={dialog === 'view'} guideline={selected} version={selectedVersion}
            isRegular={isRegular} loading={viewLoading} onClose={() => setDialog('')}
            onCreateVersion={() => setDialog('create')}
            onViewVersion={async version => {
                setViewLoading(true)
                try {
                    const hydrated = await loadAgreements(version)
                    setGuidelines(current => current.map(guideline => guideline.parentId === selectedParent ? {
                        ...guideline,
                        versions: guideline.versions.map(item => item.id === hydrated.id ? hydrated : item)
                    } : guideline))
                    setSelectedVersionId(version.id)
                } finally {
                    setViewLoading(false)
                }
            }} onIssueClick={askIssue} onAgree={agreeToVersion} />
        <CreateVersion open={dialog === 'create'} guideline={selected} onCancel={() => setDialog('view')}
            onCreate={createGuidelineVersion} />
        <IssueConfirm open={dialog === 'issue'} version={issueTarget} onCancel={() => setDialog(selected ? 'view' : '')}
            onIssue={async() => {
                const response = await updateGuidelineVersion({
                    id: issueTarget.id,
                    userId
                })
                if (response.message === 'success') {
                    await loadGuidelines(search)
                    setDialog('')
                }
            }} />
        <AgreeConfirmation open={Boolean(agreeTarget)} guideline={agreeTarget?.guideline}
            version={agreeTarget?.version} onCancel={() => setAgreeTarget(null)}
            onAgree={agreeToVersion} />
        <Dialog open={Boolean(removeTarget)} onClose={() => setRemoveTarget(null)} fullWidth maxWidth="xs">
            <DialogTitle>Remove Guideline?</DialogTitle>
            <DialogContent>Once you remove this guideline, you will not be able to access it again.</DialogContent>
            <DialogActions><Button onClick={() => setRemoveTarget(null)}>Cancel</Button>
                <Button color="error" onClick={async() => {
                    const response = await updateGuidelineVersion({
                        id: removeTarget.versions[0].id,
                        userId,
                        remove: true
                    })
                    if (response.message === 'success') {
                        await loadGuidelines(search)
                        setRemoveTarget(null)
                    }
                }}>Remove</Button>
            </DialogActions>
        </Dialog>
    </Box>
}
